import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { actions as treeActions } from 'features/tree';
import { actions } from 'features/reports/redux';

import { diffRangeItems, structure } from '../data';

const useGeneralModel = () => {
  const { selectedAgentId } = useSelector(state => state.tree, shallowEqual);
  const dispatch = useDispatch();

  const [date, setDate] = useState({
    beginDate: new Date(dayjs().add(-1, 'week')),
    endDate: new Date(dayjs()),
  });
  const [diffDate, setDiffDate] = useState({
    beginDate: new Date(dayjs().add(-2, 'week')),
    endDate: new Date(dayjs().add(-1, 'week')),
  });
  const [withDiff, setWithDiff] = useState(false);

  const getDiffDate = () => {
    if (activeDiffRange.name === 'manualCompare') {
      return {
        compareFrom: diffDate.beginDate,
        compareTo: diffDate.endDate,
      };
    }

    const begin = dayjs(date.beginDate).startOf('day');
    const end = dayjs(date.endDate).startOf('day');

    const { count = 0, type = 'day' } = activeDiffRange.value || {};

    const compareFrom = begin.subtract(count, type);
    const compareTo = end.subtract(count, type);

    return {
      compareFrom: compareFrom.toDate(),
      compareTo: compareTo.toDate(),
    };
  };

  const [activeDay, setActiveDay] = useState('');
  const [activeDiffRange, setActiveDiffRange] = useState({ name: 'manualCompare', value: 'manualCompare' });
  const [sortBy, setSortBy] = useState({ isOpen: false, type: '', direction: 'asc' });

  const handleSetWithDiff = value => {
    setWithDiff(value);
  };

  const handleSetSortBy = value => {
    setSortBy(value);
  };

  const [filters, setFilters] = useState({
    structure: structure[0],
  });

  const handleStructureChange = value => {
    const structureItem = structure.find(item => item.value === value);
    setFilters({ ...filters, structure: structureItem });
  };

  const [filterValue, setFilterValue] = useState('');

  const onChangeBeginDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate({ ...date, beginDate: value });
    }
    setActiveDay('');
  };

  const onChangeEndDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate({ ...date, endDate: value });
    }
  };

  const onChangeFilterValue = e => {
    setFilterValue(e.target.value);
  };

  const onChangeBeginDiffDate = value => {
    if (!isNaN(value.valueOf())) {
      setDiffDate(prev => ({ ...prev, beginDate: value }));
    }
    setActiveDay('');

    setActiveDiffRange({ name: 'manualCompare', value: 'manualCompare' });
  };

  const onChangeEndDiffDate = value => {
    if (!isNaN(value.valueOf())) {
      setDiffDate(prev => ({ ...prev, endDate: value }));
    }

    setActiveDiffRange({ name: 'manualCompare', value: 'manualCompare' });
  };

  const onChangeActiveDay = useCallback(
    dayFilter => {
      setActiveDay(dayFilter);
      setDate({ ...date, beginDate: dayFilter.value, endDate: dayFilter.value2 });

      if (activeDiffRange.name === 'manualCompare') {
        const diffDays = dayjs(dayFilter.value2).diff(dayFilter.value, 'd');
        setDiffDate({
          beginDate: new Date(dayjs(dayFilter.value)),
          endDate: new Date(dayjs(dayFilter.value).add(diffDays, 'day')),
        });
      }

      const diffItems = diffRangeItems[dayFilter.text || 'today'];
      if (diffItems && diffItems.length > 0) {
        setActiveDiffRange(diffItems[0]);
      }
    },
    [date, activeDay],
  );

  const onChangeDiffRange = useCallback((dayFilter, name) => {
    setActiveDiffRange({ value: { type: dayFilter.type, count: dayFilter.count }, name });
  }, []);

  const handleSearch = () => {
    dispatch(treeActions.setSearchTree(filterValue));
    if (withDiff) {
      const diffDate = getDiffDate();
      dispatch(
        actions.getStatisticProvider({
          date: { ...date },
          ...filters,
          withDiff,
          diffDate: { ...diffDate },
        }),
      );
    } else {
      dispatch(actions.getStatisticProvider({ date: { ...date }, ...filters }));
    }
  };

  const handleFormSubmit = e => {
    e.preventDefault();
    handleSearch();
  };

  const handleKeyDown = e => {
    if (e?.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  useEffect(() => {
    dispatch(treeActions.setSearchTree(''));
  }, [dispatch]);

  useEffect(() => {
    if (activeDiffRange.name !== 'manualCompare') {
      const begin = dayjs(date.beginDate).startOf('day');
      const end = dayjs(date.endDate).startOf('day');
      const daysCount = end.diff(begin, 'day');

      const { count = 0, type = 'day' } = activeDiffRange.value || {};

      const compareFrom = begin.subtract(count, type);
      const compareTo = compareFrom.add(daysCount, 'day');

      setDiffDate({
        beginDate: compareFrom.toDate(),
        endDate: compareTo.toDate(),
      });
    }
  }, [activeDiffRange, date.beginDate, date.endDate]);

  const handleGetDiff = () => {
    handleSetWithDiff(true);
    const diffDate = getDiffDate();

    dispatch(
      actions.getStatisticProvider({
        date: { ...date },
        ...filters,
        withDiff,
        diffDate: { ...diffDate },
      }),
    );
  };

  useEffect(() => {
    // dispatch(actions.getStatisticProvider({ ...date, ...filters }));
    if (withDiff) {
      handleGetDiff();
    } else {
      dispatch(actions.getStatisticProvider({ date: { ...date }, ...filters }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgentId, withDiff]);

  return {
    handleFormSubmit,
    handleKeyDown,
    handleStructureChange,
    onChangeBeginDate,
    onChangeEndDate,
    onChangeBeginDiffDate,
    onChangeEndDiffDate,
    onChangeActiveDay,
    onChangeFilterValue,
    onChangeDiffRange,
    handleSetWithDiff,
    handleGetDiff,
    handleSetSortBy,
    values: {
      activeDay,
      activeDiffRange,
      structure,
      filters,
      filterValue,
      date,
      diffDate,
      withDiff,
      sortBy,
    },
  };
};

export default useGeneralModel;
